import { useState, useRef, useEffect } from "react";
import { Icon } from "@/components/ui/Icon";
import { SUBSCRIPTIONS_ENABLED } from "@adresflow-v2/shared";

const WA_NUMBER = import.meta.env.VITE_HELP_WHATSAPP ?? "48000000000";
const WA_URL = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Cześć! Mam pytanie dotyczące AdresFlow.")}`;
const MARCIN_AVATAR = "/1741729159534.jpeg";

function MarcinAvatar({ size = 36, dot = false }: { size?: number; dot?: boolean }) {
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <img
        src={MARCIN_AVATAR}
        alt="Marcin"
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", display: "block" }}
      />
      {dot && <span className="help-id__dot" />}
    </div>
  );
}

const HELP_REPLIES = [
  {
    q: "Jak działa Home Staging?",
    a: "Wgrywasz zdjęcie wnętrza, wybierasz styl, a AI w ~30 s zwraca gotową aranżację. Każde zdjęcie to 3 kredyty.",
  },
  {
    q: "Pytanie o kredyty",
    a: SUBSCRIPTIONS_ENABLED
      ? 'Kredyty zużywasz przy generowaniu. Masz pulę w abonamencie lub możesz dokupić pakiet jednorazowo w zakładce "Plany i kredyty".'
      : 'Kredyty zużywasz przy generowaniu. Dokupisz je jednorazowo w pakietach, w zakładce "Cennik".',
  },
  {
    q: "Współpraca dla biura nieruchomości",
    a: SUBSCRIPTIONS_ENABLED
      ? "Plan Biuro i Agencja dają wspólną pulę kredytów, role i panel Marki dla całego zespołu. Napisz na WhatsApp — podpowiem co wybrać."
      : "Załóż agencję w zakładce „Agencja” — zespół dzieli wspólną pulę kredytów. Napisz na WhatsApp, jeśli potrzebujesz pomocy.",
  },
  {
    q: "Mam problem techniczny",
    a: "Opisz co się dzieje — albo napisz do nas bezpośrednio na WhatsApp, a zajmiemy się tym od razu.",
  },
];

interface Message {
  from: "me" | "marcin" | "typing";
  text?: string;
}

export function HelpWidget({ open: openProp, onOpenChange }: { open?: boolean; onOpenChange?: (v: boolean) => void } = {}) {
  // Sterowany z AppShell (żeby otworzyć panel z dolnego mobile-nav), fallback na stan wewnętrzny.
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => {
    const next = typeof v === "function" ? (v as (o: boolean) => boolean)(open) : v;
    if (onOpenChange) onOpenChange(next);
    else setOpenInternal(next);
  };
  const [thread, setThread] = useState<Message[]>([
    { from: "marcin", text: "Hej! Jestem Marcin z AdresFlow. W czym mogę Ci pomóc?" },
  ]);
  const [text, setText] = useState("");
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [thread, open]);

  const reply = (userText: string, answer: string) => {
    setThread((t) => [...t, { from: "me", text: userText }]);
    setTimeout(() => setThread((t) => [...t, { from: "typing" }]), 250);
    setTimeout(
      () =>
        setThread((t) => [
          ...t.filter((m) => m.from !== "typing"),
          { from: "marcin", text: answer },
        ]),
      1100,
    );
  };

  const pickChip = (item: { q: string; a: string }) => reply(item.q, item.a);

  const send = () => {
    const v = text.trim();
    if (!v) return;
    setText("");
    reply(v, "Dzięki! Najszybciej odpiszę przez WhatsApp — kliknij przycisk poniżej 👇");
  };

  const askedQs = thread.filter((m) => m.from === "me").map((m) => m.text);
  const chips = HELP_REPLIES.filter((r) => !askedQs.includes(r.q));

  return (
    <div className="help">
      {open && (
        <div className="help-panel" role="dialog" aria-label="Pomoc">
          {/* Head */}
          <div className="help-panel__head">
            <div className="help-id">
              <div className="help-id__av">
                <MarcinAvatar size={36} dot />
              </div>
              <div>
                <div className="help-id__name">
                  Marcin <span>z AdresFlow</span>
                </div>
                <div className="help-id__status">Online · odpowiada w ~5 min</div>
              </div>
            </div>
            <button className="help-close" onClick={() => setOpen(false)}>
              <Icon name="x" />
            </button>
          </div>

          {/* Body */}
          <div className="help-body" ref={bodyRef}>
            {thread.map((m, i) =>
              m.from === "typing" ? (
                <div key={i} className="help-msg marcin">
                  <div className="help-bubble help-typing">
                    <span /><span /><span />
                  </div>
                </div>
              ) : (
                <div key={i} className={`help-msg ${m.from}`}>
                  <div className="help-bubble">{m.text}</div>
                </div>
              ),
            )}

            {chips.length > 0 && (
              <div className="help-chips">
                {chips.map((c, i) => (
                  <button key={i} className="help-chip" onClick={() => pickChip(c)}>
                    {c.q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Text input */}
          <div className="help-input">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Napisz wiadomość…"
            />
            <button
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                display: "grid",
                placeItems: "center",
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={send}
            >
              <Icon name="send" style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* WhatsApp CTA */}
          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="help-wa"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            Napisz na WhatsApp — odpiszemy od razu
          </a>
        </div>
      )}

      {/* FAB */}
      <button
        className={`help-fab ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Pomoc"
      >
        {open ? (
          <Icon name="x" />
        ) : (
          <>
            <span className="help-fab__av">
              <MarcinAvatar size={32} />
            </span>
            <span className="help-fab__dot" />
            <span className="help-fab__label">Pomoc</span>
          </>
        )}
      </button>
    </div>
  );
}
